import { useMemo } from 'react'
import type { ChartData, ChartOptions, Plugin } from 'chart.js'
import { Line } from 'react-chartjs-2'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import '@/features/campaigns/charts/registerCharts'
import {
  SENSEMAKER_OVERVIEW_BURN_CHART_AXIS_TICK_COLOR,
  SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR,
  SENSEMAKER_OVERVIEW_BURN_CHART_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_BURN_CHART_PADDING_PX,
  SENSEMAKER_OVERVIEW_BURN_CHART_RADIUS_PX,
  SENSEMAKER_OVERVIEW_BURN_CHART_SHADOW,
  SENSEMAKER_OVERVIEW_BURN_CHART_TITLE_COLOR,
  SENSEMAKER_OVERVIEW_BURN_CHART_TITLE_TO_GRAPH_GAP_PX,
} from '../../lib/sensemaker-overview-layout'
import {
  TOKEN_WARNING_THRESHOLD_PERCENT,
  type SensemakerTokenMock,
} from '../../lib/sensemaker-overview-token.mock'
import { SensemakerTokenBurnChartEmptyState } from './SensemakerTokenBurnChartEmptyState'

interface Props {
  tokenMock: SensemakerTokenMock
}

const chartFont = {
  family: 'Inter, sans-serif',
  size: 11,
  weight: 'bold' as const,
}

export function SensemakerTokenBurnTrendChart({ tokenMock }: Readonly<Props>) {
  const thresholdPlugin = useMemo<Plugin<'line'>>(
    () => ({
      id: 'tokenWarningLine',
      afterDraw(chart) {
        const { ctx, chartArea, scales } = chart
        const y = scales.y.getPixelForValue(TOKEN_WARNING_THRESHOLD_PERCENT)
        if (y < chartArea.top || y > chartArea.bottom) return
        ctx.save()
        ctx.strokeStyle = '#ef4444'
        ctx.setLineDash([6, 4])
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(chartArea.left, y)
        ctx.lineTo(chartArea.right, y)
        ctx.stroke()
        ctx.restore()
      },
    }),
    [],
  )

  const data = useMemo<ChartData<'line'>>(
    () => ({
      labels: tokenMock.burnTrend.map((p) => p.weekLabel),
      datasets: [
        {
          label: 'Token balance %',
          data: tokenMock.burnTrend.map((p) => p.balancePercent),
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.08)',
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointBackgroundColor: '#2563eb',
          borderWidth: 2,
        },
      ],
    }),
    [tokenMock.burnTrend],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 4, right: 8, bottom: 4, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const index = items[0]?.dataIndex
              if (typeof index !== 'number') return ''
              return tokenMock.burnTrend[index]?.weekLabel ?? ''
            },
            label: (ctx) => `${ctx.parsed.y}% balance`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR,
            drawBorder: false,
          },
          ticks: {
            color: SENSEMAKER_OVERVIEW_BURN_CHART_AXIS_TICK_COLOR,
            font: chartFont,
            maxRotation: 0,
          },
          title: {
            display: true,
            text: 'Week',
            color: '#000',
            font: { ...chartFont },
          },
        },
        y: {
          min: 0,
          max: 100,
          grid: {
            color: SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR,
            drawBorder: false,
          },
          ticks: {
            color: SENSEMAKER_OVERVIEW_BURN_CHART_AXIS_TICK_COLOR,
            font: chartFont,
            callback: (v) => `${v}%`,
            stepSize: 20,
          },
          title: {
            display: true,
            text: 'Token balance',
            color: '#000',
            font: chartFont,
          },
        },
      },
    }),
    [tokenMock.burnTrend],
  )

  const hasData = tokenMock.burnTrend.length > 0

  return (
    <Box
      sx={{
        borderRadius: `${SENSEMAKER_OVERVIEW_BURN_CHART_RADIUS_PX}px`,
        boxShadow: SENSEMAKER_OVERVIEW_BURN_CHART_SHADOW,
        p: `${SENSEMAKER_OVERVIEW_BURN_CHART_PADDING_PX}px`,
        bgcolor: '#fff',
        height: SENSEMAKER_OVERVIEW_BURN_CHART_HEIGHT_PX,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid="sensemaker-token-burn-chart"
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          sx={{
            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: 'normal',
            color: SENSEMAKER_OVERVIEW_BURN_CHART_TITLE_COLOR,
          }}
        >
          Token Burn Trend
        </Typography>
      </Box>

      {!hasData ? (
        <SensemakerTokenBurnChartEmptyState />
      ) : (
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            mt: `${SENSEMAKER_OVERVIEW_BURN_CHART_TITLE_TO_GRAPH_GAP_PX}px`,
          }}
        >
          <Line data={data} options={options} plugins={[thresholdPlugin]} />
          <Typography
            sx={{
              position: 'absolute',
              right: 8,
              top: 4,
              fontSize: '11px',
              fontWeight: 600,
              color: '#ef4444',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Warning threshold ({TOKEN_WARNING_THRESHOLD_PERCENT}%)
          </Typography>
        </Box>
      )}
    </Box>
  )
}
